import { useCallback, useMemo, useState, type DragEvent } from 'react';
import { useApi } from '../../engine/api/context';
import { useMeta, useUpdate } from '../../engine/api/hooks';
import { useWidgets } from '../../engine/api/context';
import { ApiError } from '../../engine/api/client';
import type { FieldMeta } from '../../engine/meta/types';
import { DataGrid } from '../../engine/views/DataGrid';
import { columnsFromMeta } from '../../engine/screens/columns';
import { IconButton } from '../../engine/design/primitives/Button';
import { EmptyState } from '../../engine/design/primitives/Misc';
import { Icon } from '../../engine/design/icons';
import { useToast } from '../../engine/design/primitives/Toast';
import { depthOf, useProjectDetail, type TaskRow } from './data';

// Table view: the engine DataGrid over the project's tasks, grouped by phase (roll-up header
// per group), inline edit of status/assignee/dates/estimate through the field registry,
// subtask expand/collapse, and reorder by dragging the grip onto another row (or the
// keyboard arrows on the grip) → POST /project-tasks/:id/reorder with the fractional index.

// Index signature so the engine's generic grid (Record<string, unknown> rows) accepts it.
type Row = TaskRow & { phase_name: string; _depth: number; _children: number } & Record<string, unknown>;

export function TasksTable({ projectId, onOpen, activeId }: { projectId: string; onOpen: (id: string) => void; activeId?: string | null }) {
  const detail = useProjectDetail(projectId);
  const meta = useMeta('project-tasks').data;
  const widgets = useWidgets();
  const api = useApi();
  const toast = useToast();
  const update = useUpdate<TaskRow>('project-tasks', { toast: { success: 'Saved' }, onSuccess: detail.refresh });
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [over, setOver] = useState<string | null>(null);

  const rows = useMemo<Row[]>(() => {
    const tasks = detail.data?.tasks ?? [];
    const byId = new Map(tasks.map((t) => [t.id, t]));
    const childCount = new Map<string, number>();
    for (const t of tasks) if (t.parent_task_id) childCount.set(t.parent_task_id, (childCount.get(t.parent_task_id) ?? 0) + 1);
    const hidden = (t: TaskRow): boolean => {
      let cur = t.parent_task_id;
      while (cur) {
        if (collapsed.has(cur)) return true;
        cur = byId.get(cur)?.parent_task_id ?? null;
      }
      return false;
    };
    // Depth-first within each phase so subtasks sit under their parent.
    const out: Row[] = [];
    const visit = (parent: string | null, phase: string | null) => {
      for (const t of tasks) {
        if (t.parent_task_id !== parent || (parent === null && t.phase_id !== phase)) continue;
        if (!hidden(t)) out.push({ ...t, phase_name: t.phase_name ?? 'No phase', _depth: depthOf(t, byId), _children: childCount.get(t.id) ?? 0 });
        visit(t.id, phase);
      }
    };
    const phaseIds = [...new Set(tasks.map((t) => t.phase_id))];
    for (const p of phaseIds) visit(null, p);
    return out;
  }, [detail.data?.tasks, collapsed]);

  const reorder = useCallback(
    async (id: string, body: Record<string, unknown>) => {
      try {
        await api.resource('project-tasks').action('reorder', { id, body });
        detail.refresh();
      } catch (e) {
        toast({ kind: 'error', title: 'Could not reorder', detail: e instanceof ApiError ? e.detail : String(e) });
      }
    },
    [api, detail, toast],
  );

  const siblingsOf = useCallback((r: Row) => rows.filter((x) => x.phase_id === r.phase_id && x.parent_task_id === r.parent_task_id), [rows]);
  const moveBy = (r: Row, dir: -1 | 1) => {
    const sibs = siblingsOf(r);
    const i = sibs.findIndex((x) => x.id === r.id);
    const target = sibs[i + dir];
    if (!target) return;
    void reorder(r.id, dir < 0 ? { before_id: target.id } : { after_id: target.id });
  };
  const onDrop = (e: DragEvent, target: Row) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id || id === target.id) return;
    void reorder(id, { before_id: target.id });
  };

  const columns = useMemo(() => {
    if (!meta) return [];
    return columnsFromMeta(meta, [
      {
        field: '_grip',
        label: '',
        width: 84,
        sortable: false,
        render: (row) => {
          const r = row as unknown as Row;
          return (
            <span
              className={['hf-prj-grip', over === r.id && 'over'].filter(Boolean).join(' ')}
              draggable
              aria-label={`Reorder ${r.ref}`}
              onClick={(e) => e.stopPropagation()}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', r.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (over !== r.id) setOver(r.id);
              }}
              onDragLeave={() => setOver((o) => (o === r.id ? null : o))}
              onDrop={(e) => onDrop(e, r)}
            >
              <Icon name="grip-vertical" size={14} />
              <IconButton icon="arrow-up" label={`Move ${r.ref} up`} size="sm" variant="ghost" onClick={() => moveBy(r, -1)} />
              <IconButton icon="arrow-down" label={`Move ${r.ref} down`} size="sm" variant="ghost" onClick={() => moveBy(r, 1)} />
            </span>
          );
        },
      },
      { field: 'ref', width: 120 },
      {
        field: 'title',
        width: 320,
        render: (row) => {
          const r = row as unknown as Row;
          return (
            <span className="hf-prj-title" style={{ paddingLeft: r._depth * 18 }}>
              {r._children > 0 ? (
                <button
                  type="button"
                  className="toggle"
                  aria-label={collapsed.has(r.id) ? `Expand ${r._children} subtasks` : `Collapse ${r._children} subtasks`}
                  aria-expanded={!collapsed.has(r.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCollapsed((s) => {
                      const n = new Set(s);
                      if (n.has(r.id)) n.delete(r.id);
                      else n.add(r.id);
                      return n;
                    });
                  }}
                >
                  <Icon name={collapsed.has(r.id) ? 'chevron-right' : 'chevron-down'} size={12} />
                </button>
              ) : (
                <span style={{ width: 18 }} aria-hidden />
              )}
              {r.kind !== 'task' && <Icon className="kind" name={r.kind === 'milestone' ? 'flag' : r.kind === 'approval' ? 'check-circle' : 'alert-triangle'} size={13} label={r.kind} />}
              <span className="hf-cell-trunc">{r.title}</span>
            </span>
          );
        },
      },
      'status',
      'assignee_tech_id',
      'start_date',
      'due_date',
      'estimate_minutes',
      'actual_minutes',
      'priority',
      { field: 'phase_name', label: 'Phase', hidden: true },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, collapsed, over]);

  if (!meta) return null;
  const cellEdit = async (row: Row, field: FieldMeta, value: unknown, previous: unknown) => {
    await update.mutateAsync({ id: row.id, body: { [field.name]: value } as Partial<TaskRow>, previous: { [field.name]: previous } as Partial<TaskRow> });
  };
  return (
    <DataGrid<Row>
      aria-label="Tasks"
      meta={meta}
      columns={columns}
      rows={rows}
      keyOf={(r) => r.id}
      loading={detail.isLoading}
      error={detail.error}
      empty={<EmptyState title="No tasks yet" hint="Add the first task or create the project from a template." icon="check-square" />}
      onRowClick={(r) => onOpen(r.id)}
      activeRowId={activeId ?? null}
      onCellEdit={cellEdit}
      groupBy="phase_name"
      widgets={widgets}
      exportName="tasks"
    />
  );
}
